import type { ComputedRef, MaybeRefOrGetter, PropType, Ref } from "vue";

import { until, whenever } from "@vueuse/core";
import { nanoid } from "nanoid";
import { computed, reactive, ref, toRef, toValue, watch } from "vue";

import type { Color } from "@/lib/components/types";
import type {
  EventsCallback,
  ValidationRule,
  ValidationRuleHydrated,
} from "@/lib/validation/validation.types";

import {
  emitsDefinition,
  propsDefinition,
  propsToDefaults,
} from "@/lib/composables/componentComposable";
import { useDelayedLoading } from "@/lib/composables/useDelayedLoading.ts";
import { mergeReactive } from "@/lib/helpers/reactivity";
import { aggressive } from "@/lib/validation/events";
import { useValidationState } from "@/lib/validation/ValidationProvider/useValidationState.ts";

import { useObserverInject } from "./useObserverInject";
import { useRules } from "./useRules";

type ValidateEvent = (event: string) => Promise<void>;
type ValidateListeners = Record<string, () => Promise<void> | undefined>;

const useValidationProviderScoped = <ModelValue>() =>
  propsDefinition({
    required: { type: Boolean, default: false, required: false },
    errorLabel: { type: String, required: false },
    label: { type: String, required: false },
    name: { type: String, required: true },
    rules: {
      type: Array as PropType<ValidationRule<ModelValue>[]>,
      default: () => [],
    },
    validationEvents: {
      type: [Array, Function] as PropType<
        EventsCallback<ModelValue> | string[]
      >,
      default: () => aggressive,
      required: false,
    },
  });

const useValidationProviderProps = useValidationProviderScoped;

const useValidationProviderEmits = emitsDefinition(["validationError"]);

type UseValidationProviderProps<ModelValue> = {
  errorLabel?: string;
  label?: string;
  name: string;
  required?: boolean;
  rules?: ValidationRule<NoInfer<ModelValue>>[];
  validationEvents?: EventsCallback<NoInfer<ModelValue>> | string[];
};
type UseValidationProviderEmits = (
  event: "validationError",
  value: { error: string | null; name: string },
) => void;

type UseValidationProviderReturn = {
  allRulesValid: Ref<boolean>;
  blockingRulesValid: Ref<boolean>;
  displayInvalid: Ref<boolean>;
  displayValid: Ref<boolean>;
  displayValidating: Ref<boolean>;
  error: Ref<string | null>;
  failedRule: ComputedRef<ValidationRuleHydrated | null>;
  isValidating: Ref<boolean>;
  validateAll: () => Promise<void>;
  validateEvent: ValidateEvent;
  validationListeners: Ref<ValidateListeners>;
  validationProviderId: string;
};

function mergeErrorProps(
  ...errorProps: {
    color?: Color;
    component?: string;
    error: string | null;
  }[]
) {
  return computed(() => errorProps.find(({ error }) => !!error));
}

function useListeners(
  validateEvent: (event: string) => Promise<void>,
  allRules: ComputedRef<ValidationRuleHydrated[]>,
) {
  const flatValidationEvents = computed(() => {
    return [...new Set(allRules.value.flatMap(({ events }) => events.value))];
  });

  const validationListeners = computed<
    Record<string, () => Promise<void> | undefined>
  >(() => {
    return Object.fromEntries(
      flatValidationEvents.value
        .filter(
          (eventName: string) =>
            !["update:modelValue", "created"].includes(eventName),
        )
        .map((eventName: string) => [
          eventName,
          () => validateEvent(eventName),
        ]),
    );
  });

  return { flatValidationEvents, validationListeners };
}

function toErrorProps({
  validationProviderId,
  failedRule,
  error,
}: Pick<
  UseValidationProviderReturn,
  "error" | "failedRule" | "validationProviderId"
>) {
  const errorId = computed(() =>
    error.value ? validationProviderId : undefined,
  );

  return {
    errorComponent: toRef(() => failedRule.value?.component),
    errorId,
    errorProps: reactive({
      error,
      color: toRef(() => failedRule.value?.color),
      component: toRef(() => failedRule.value?.component),
      // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment
      params: toRef<any>(() => failedRule.value?.params),
    }),
  };
}

function toInputProps({
  displayValid,
  displayInvalid,
  failedRule,
  displayValidating,
}: Pick<
  UseValidationProviderReturn,
  "displayInvalid" | "displayValid" | "displayValidating" | "failedRule"
>) {
  return reactive({
    valid: displayValid,
    loading: displayValidating,
    invalid: displayInvalid,
    color: toRef(() => failedRule.value?.color),
  });
}

function useValidationProvider<ModelValue>(
  modelValue: MaybeRefOrGetter<ModelValue>,
  options: UseValidationProviderProps<NoInfer<ModelValue>>,
  emit: UseValidationProviderEmits = () => null,
) {
  const props = mergeReactive(
    propsToDefaults(useValidationProviderProps()),
    options,
  );

  const validationProviderId = nanoid(10);

  // Mutable state, all state is derived from this using computed props
  const triggeredEvents = ref(new Set<string>());

  const allRules = useRules(modelValue, props, triggeredEvents);

  const { allRulesValid, blockingRulesValid, error, failedRule, isValidating } =
    useValidationState({
      errorLabel: computed(() => props.errorLabel ?? props.label ?? props.name),
      modelValue,
      allRules,
    });

  async function validateEvent(event: string) {
    triggeredEvents.value.add(event);
    await until(isValidating).toBe(false);
  }
  const validateAll = () => validateEvent("all");

  useObserverInject(
    toRef(() => props.name),
    validationProviderId,
    modelValue,
    validateAll,
    blockingRulesValid,
    isValidating,
  );

  const { flatValidationEvents, validationListeners } = useListeners(
    validateEvent,
    allRules,
  );

  watch(
    toRef(modelValue),
    () => {
      triggeredEvents.value.clear();
      if (flatValidationEvents.value.includes("update:modelValue")) {
        void validateEvent("update:modelValue");
      }
    },
    { deep: true },
  );
  void validateEvent("created");

  whenever(error, (error) => {
    emit("validationError", { error, name: props.name });
  });

  const displayValidation = {
    displayInvalid: computed(() => error.value !== null),
    displayValid: computed(() => !!toValue(modelValue) && allRulesValid.value),
    displayValidating: useDelayedLoading(isValidating),
  };

  // TODO: make this the only return value, the others are for backwards compatibility
  const returnValue: UseValidationProviderReturn = {
    ...displayValidation,
    allRulesValid,
    blockingRulesValid,
    error,
    failedRule,
    isValidating,
    validateAll,
    validateEvent,
    validationListeners,
    validationProviderId,
  };

  return {
    ...returnValue,
    ...toErrorProps(returnValue),
    inputProps: toInputProps(returnValue),
  };
}

export type {
  UseValidationProviderEmits,
  UseValidationProviderProps,
  UseValidationProviderReturn,
  ValidateEvent,
};
export {
  mergeErrorProps,
  useValidationProvider,
  useValidationProviderEmits,
  useValidationProviderProps,
  useValidationProviderScoped,
};
